/**
 * Sidebar.tsx — navy + gold left nav. Brand block at top, menu in the
 * middle, user chip + logout at the bottom.
 *
 * Visually identical to the TNSC Sidebar — only the brand mark text + name
 * change. Menu entries come from `menu.ts`.
 */

import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import Ico, { type IcoName } from '../components/Ico';
import { MENU, isSection, type MenuItem } from '../menu';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logoutThunk, selectUser } from '../redux/slices/auth';

/**
 * Active-link matching with two escape hatches:
 *
 * 1. `MenuItem.activePrefix` — light up when the current URL starts with the
 *    given prefix (so /banks/:orgId keeps "Banks" highlighted).
 *
 * 2. Sibling-item exclusion — when another menu item has a longer matching
 *    path that's also a prefix of the current URL, defer to it. This lets
 *    "Bank Submission Status" (path /banks/submission-status) win over the
 *    "Banks" item (activePrefix /banks) when the user is on the submission
 *    status page.
 */
function isItemActive(item: MenuItem, pathname: string, all: MenuItem[]): boolean {
	const match = item.activePrefix
		? pathname === item.activePrefix || pathname.startsWith(item.activePrefix + '/')
		: pathname === item.path;
	if (!match) return false;
	for (const other of all) {
		if (other.k === item.k) continue;
		// If a sibling has a more-specific path that also matches, that one wins.
		if (other.path === pathname || pathname.startsWith(other.path + '/')) {
			if (other.path.length > (item.activePrefix ?? item.path).length) return false;
		}
	}
	return true;
}

export default function Sidebar() {
	const user = useAppSelector(selectUser);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const items = MENU.filter((m): m is MenuItem => !isSection(m));

	const initials = (user?.fullName || user?.username || 'U')
		.split(' ')
		.map((s) => s[0])
		.filter(Boolean)
		.slice(0, 2)
		.join('')
		.toUpperCase();

	const handleLogout = async () => {
		await dispatch(logoutThunk());
		navigate('/login', { replace: true });
	};

	return (
		<aside className="sidebar" aria-label="Primary navigation">
			<div className="sidebar-brand">
				<div className="brand-mark">U</div>
				<div>
					<div className="brand-title">UCB Dashboard</div>
					<div className="brand-sub">Submission Monitor</div>
				</div>
			</div>
			<nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{MENU.map((it, i) =>
					isSection(it) ? (
						<div key={`s-${i}`} className="nav-section-title">
							{it.section}
						</div>
					) : (
						<NavLink
							key={it.k}
							to={it.path}
							end
							className={
								'nav-item' + (isItemActive(it, pathname, items) ? ' active' : '')
							}
						>
							<Ico name={it.icon as IcoName} />
							<span>{it.label}</span>
							{it.badge && (
								<span
									className={'nav-badge' + (it.badge === 'NEW' ? '' : ' alert')}
								>
									{it.badge}
								</span>
							)}
						</NavLink>
					),
				)}
			</nav>
			<div className="sidebar-foot">
				<div className="avatar" aria-hidden="true">
					{initials}
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div style={{ color: '#fff', fontSize: 12.5, fontWeight: 600 }}>
						{user?.fullName || user?.username || 'Signed in'}
					</div>
					<div style={{ color: 'rgba(220,230,244,0.55)', fontSize: 11 }}>
						{(user?.role || 'viewer').toUpperCase()}
					</div>
				</div>
				<button
					type="button"
					className="icon-btn"
					onClick={handleLogout}
					title="Logout"
					aria-label="Logout"
					style={{
						background: 'transparent',
						border: '1px solid rgba(255,255,255,0.1)',
						color: 'rgba(255,255,255,0.7)',
					}}
				>
					<Ico name="logout" size={14} />
				</button>
			</div>
		</aside>
	);
}
